import { useMutation, useQuery } from '@apollo/client';
import { cloneDeep, has, isEmpty, isFunction } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useWatch } from 'react-hook-form';
import { convertUnits } from '../../utils/formatting';
import warning from '../../utils/logger';
import { FieldNotFoundError } from '../pages/measuringPoint/schema-utils';
import { NotFoundError } from '../pages/NotFoundPage';
import { NON_FIELD_ERRORS } from './ErrorToNotificationsLink';
import { extractEnabledFields, OVERRIDES_NAME } from './utils';

function useEntity(getEntityQuery, getResolverName, id, skip, onDoesNotExist) {
    const { showBoundary } = useErrorBoundary();

    const response = useQuery(getEntityQuery, {
        variables: { id },
        skip,
        onError(error) {
            if (
                error.graphQLErrors.some(
                    (subError) => subError.extensions.code === 'ENTITY_UNKNOWN'
                )
            ) {
                isFunction(onDoesNotExist) && onDoesNotExist();
                return;
            }

            showBoundary(error);
        },
    });

    return useMemo(
        () => ({
            ...response,
            data: response.data?.[getResolverName] ?? null,
        }),
        [response, getResolverName]
    );
}

// All hooks that do things with a project are wrapped inside this component
// that will be included when needed. Using a component instead of just a hook
// is to overcome the hooks cannot be optional violation.
const OptionalParentDataLink = (dataLinkProps) => {
    const { queries, convertDataIntoFormValues, setParentValues } = dataLinkProps;

    const { getParentEntityQuery, getParentResolverName, parentEntityFieldName } = queries;

    const parentEntityId = useWatch({
        defaultValue: null,
        name: parentEntityFieldName,
        disable: !parentEntityFieldName,
    });

    const { data: parentEntityData } = useEntity(
        getParentEntityQuery,
        getParentResolverName,
        parentEntityId,
        !getParentEntityQuery || !parentEntityId
    );

    useEffect(() => {
        // In cases where we have a `parentEntityId` but the query result remains null
        // (due to an ENTITY_NOT_FOUND error or similar), we map the `null` to an empty
        // object, indicating to the form fields that the override checkboxes need to be
        // shown.
        // This will prevent the form from being able to show the project values, but upon
        // saving, the backend will override the parent/project values for fields that were
        // not overridden, allowing the form to function correctly in the sense that the data
        // stored in the database is 'correct'.
        // This is a fallback/workaround to avoid showing an error page for these kinds of
        // situations until https://github.com/omnidots/website/issues/8205 is resolved.
        const parentValues = parentEntityId ? parentEntityData ?? {} : null;

        setParentValues(parentValues && convertDataIntoFormValues(parentValues));
    }, [parentEntityId, parentEntityData, setParentValues, convertDataIntoFormValues]);
    return null;
};

const GraphQLDataLink = (dataLinkProps) => {
    const { showBoundary } = useErrorBoundary();

    const {
        queries,
        pk,
        setOnSubmitFunction,
        convertDataIntoFormValues,
        setError,
        setLoading,
        setValues,
        onSubmitSuccessful,
        saveIsInProgressCallback,
        readOnlyFields,
        formSchema,
    } = dataLinkProps;

    const {
        objectName,
        getEntityQuery,
        getResolverName,
        updateEntityQuery,
        updateEntityQueryName,
        createEntityQuery,
        createEntityQueryName,
        getParentEntityQuery,
    } = queries;

    const isAddMode = !pk;

    // If there is a parent entity query, this entity has an optional parent (project).
    const entityHasOptionalParent = !!getParentEntityQuery;

    function setGlobalError(key, message) {
        setError(`${NON_FIELD_ERRORS}.${key}`, { type: 'server', message });
    }

    const [mutationQuery, mutationQueryName] = isAddMode
        ? [createEntityQuery, createEntityQueryName]
        : [updateEntityQuery, updateEntityQueryName];

    const [submitMutate, { loading }] = useMutation(mutationQuery, {
        onCompleted(result) {
            saveIsInProgressCallback(false);
            const data = result?.[mutationQueryName];

            const mutationErrors = data?.errors;

            if (!isEmpty(mutationErrors)) {
                mutationErrors.forEach(({ field, message }, index) => {
                    if (field) {
                        setError(field, { type: 'server', message });
                        return;
                    }

                    setGlobalError(`apiError${index}`, message);
                });
                return;
            }

            onSubmitSuccessful({
                id: data[objectName].id,
            });
        },
        onError(error) {
            saveIsInProgressCallback(false);
            warning(error);
            setGlobalError('serverError', gettext('GENERIC_SERVER_ERROR'));
        },
    });

    const onSubmit = useCallback(
        (values) => {
            if (loading) {
                // We're already saving, no need to do it again!
                return;
            }

            saveIsInProgressCallback(true);

            const payload = cloneDeep(values);

            readOnlyFields.forEach((name) => {
                delete payload[name];
            });

            delete payload.sensor;
            delete payload.alarmValue;
            delete payload.coordinates;
            delete payload.swarmType;

            if (entityHasOptionalParent) {
                payload.overriddenFields = extractEnabledFields(payload[OVERRIDES_NAME]);
            }
            delete payload[OVERRIDES_NAME];

            if (has(payload, 'project') && payload.project === '') {
                payload.project = null;
            }

            delete payload.permission;

            // Set a default value of 0 for (null)/('') values.
            if (has(payload, 'dailyValueSettings')) {
                payload.dailyValueSettings.triggers.forEach((item, index) => {
                    if (!item.trigger) payload.dailyValueSettings.triggers[index].trigger = 0;
                    if (!item.days) payload.dailyValueSettings.triggers[index].days = 0;
                });
            }

            if (has(payload, 'ldenGuideline')) {
                if (payload.ldenGuideline !== 'LdenGuidelineCustom') {
                    payload.notificationConfig.ldenSchedules = [];
                }
            }

            if (has(payload, 'soundClipAlarmSource')) {
                if (payload.soundClipAlarmSource === 'disabled') {
                    delete payload.tracePostTrigger;
                    delete payload.tracePreTrigger;
                    delete payload.traceSaveLevel;
                }
            }

            // Apply unit conversions.
            Object.keys(payload).forEach((name) => {
                try {
                    const field = formSchema.fields.getByName(name);

                    payload[name] = convertUnits(field, payload[name], true);
                } catch (error) {
                    // Some fields in the form do not exist in the backend schema.
                    // We expect these to be not found.
                    if (!(error instanceof FieldNotFoundError)) {
                        throw error;
                    }
                }
            });

            const mutationOptions = {
                variables: {
                    input: payload,
                },
            };

            if (!isAddMode) {
                // Add ID for the update.
                mutationOptions.variables.input.id = pk;
            }

            submitMutate(mutationOptions);
        },
        [
            isAddMode,
            entityHasOptionalParent,
            pk,
            submitMutate,
            readOnlyFields,
            formSchema.fields,
            loading,
            saveIsInProgressCallback,
        ]
    );

    useEffect(() => {
        setOnSubmitFunction(() => onSubmit);
    }, [setOnSubmitFunction, onSubmit]);

    const { loading: getEntityLoading, data: entityData } = useEntity(
        getEntityQuery,
        getResolverName,
        pk,
        isAddMode,
        () => {
            showBoundary(new NotFoundError());
        }
    );

    useEffect(() => {
        setLoading(() => getEntityLoading);
    }, [setLoading, getEntityLoading]);

    // Set form values when we received the data.
    useEffect(() => {
        if (entityData) {
            setValues(convertDataIntoFormValues(entityData));
        }
    }, [entityData, entityHasOptionalParent, setValues, convertDataIntoFormValues]);

    return <>{getParentEntityQuery && <OptionalParentDataLink {...dataLinkProps} />}</>;
};

export default GraphQLDataLink;
